import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  ArrowUpDown,
  CalendarDays,
  Check,
  CheckCircle2,
  CircleSlash,
  Columns3,
  Eye,
  FileSpreadsheet,
  Loader2,
  Mail,
  Phone,
  Plus,
  RefreshCcw,
  Rows3,
  StickyNote,
  Trash2,
  UploadCloud,
  UserRound,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { useTerminology } from "@/contexts/TerminologyContext";
import { supabase } from "@/integrations/supabase/client";
import {
  ALL_FIELDS,
  REQUIRED_FIELDS,
  applyMapping,
  missingImportFields,
  autoMapHeaders,
  type ContactField,
  type ParsedRow,
} from "@/lib/contactsImportParse";
import { cn } from "@/lib/utils";
import { readEdgeError } from "@/lib/edgeError";
import Papa from "papaparse";
import * as XLSX from "xlsx";

type ImportStep = 1 | 2 | 3 | 4;

type PreparedRow = {
  row_index: number;
  name: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  last_booking_date: string | null;
};

type ValidationRow = {
  row_index: number;
  ok: boolean;
  action: "create" | "update" | "skip" | "error";
  match_reason: "email" | "phone" | "email_and_phone" | null;
  changed_fields: string[];
  errors: string[];
  warnings: string[];
};

type ValidationResult = {
  total: number;
  ok_count: number;
  error_count: number;
  create_count: number;
  update_count: number;
  skip_count: number;
  results: ValidationRow[];
};

type ImportSummary = {
  inserted: number;
  updated: number;
  error_count: number;
  skipped_count: number;
  profile_error_count: number;
  insert_errors?: { message: string; details?: string }[];
};

type ReviewSort = "raw" | "row" | "errors";

type ReviewRow = {
  rowIndex: number;
  raw: string;
  mapped: string;
  hasError: boolean;
  isSkipped: boolean;
};

type ImportDraft = {
  step: ImportStep;
  file: File | null;
  headers: string[];
  rows: ParsedRow[];
  mapping: Record<ContactField, string | null>;
  selectedHeader: string | null;
  reviewField: ContactField;
  reviewSort: ReviewSort;
  rowOverrides: Record<number, Partial<Record<ContactField, string | null>>>;
  skippedFieldsByRow: Record<number, ContactField[]>;
  skippedRowIndices: number[];
  validation: ValidationResult | null;
  preparedPayload: PreparedRow[];
  importSummary: ImportSummary | null;
};

const customerImportDrafts = new Map<string, ImportDraft>();

const STEPS: { value: ImportStep; label: string }[] = [
  { value: 1, label: "Upload file" },
  { value: 2, label: "Map columns" },
  { value: 3, label: "Review values" },
  { value: 4, label: "Preview import" },
];

const UNMAPPED = "__unmapped__";

const FIELD_ICONS: Record<ContactField, typeof UserRound> = {
  name: UserRound,
  email: Mail,
  phone: Phone,
  notes: StickyNote,
  last_booking_date: CalendarDays,
};

const emptyMapping = () =>
  ALL_FIELDS.reduce(
    (result, field) => ({ ...result, [field.key]: null }),
    {} as Record<ContactField, string | null>,
  );

interface CustomerImportPageProps {
  embedded?: boolean;
  onCancel?: () => void;
  onComplete?: () => void;
}

export default function CustomerImportPage({
  embedded = false,
  onCancel,
  onComplete,
}: CustomerImportPageProps = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { businessId } = useAuth();
  const { customer, customers } = useTerminology();
  const inputRef = useRef<HTMLInputElement>(null);
  const draftKey = `${businessId ?? "pending-business"}${embedded ? ":onboarding" : ""}`;
  const savedDraft = customerImportDrafts.get(draftKey);

  const [step, setStep] = useState<ImportStep>(() => savedDraft?.step ?? 1);
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState<File | null>(() => savedDraft?.file ?? null);
  const [headers, setHeaders] = useState<string[]>(() => savedDraft?.headers ?? []);
  const [rows, setRows] = useState<ParsedRow[]>(() => savedDraft?.rows ?? []);
  const [mapping, setMapping] = useState<Record<ContactField, string | null>>(
    () => savedDraft?.mapping ?? emptyMapping(),
  );
  const [selectedHeader, setSelectedHeader] = useState<string | null>(
    () => savedDraft?.selectedHeader ?? null,
  );
  const [reviewField, setReviewField] = useState<ContactField>(
    () => savedDraft?.reviewField ?? "name",
  );
  const [reviewSort, setReviewSort] = useState<ReviewSort>(
    () => savedDraft?.reviewSort ?? "raw",
  );
  const [rowOverrides, setRowOverrides] = useState<
    Record<number, Partial<Record<ContactField, string | null>>>
  >(() => savedDraft?.rowOverrides ?? {});
  const [skippedFieldsByRow, setSkippedFieldsByRow] = useState<
    Record<number, ContactField[]>
  >(() => savedDraft?.skippedFieldsByRow ?? {});
  const [skippedRowIndices, setSkippedRowIndices] = useState<number[]>(
    () => savedDraft?.skippedRowIndices ?? [],
  );
  const [parseError, setParseError] = useState<string | null>(null);
  const [validation, setValidation] = useState<ValidationResult | null>(
    () => savedDraft?.validation ?? null,
  );
  const [preparedPayload, setPreparedPayload] = useState<PreparedRow[]>(
    () => savedDraft?.preparedPayload ?? [],
  );
  const [validating, setValidating] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(
    () => savedDraft?.importSummary ?? null,
  );
  const [editingReviewValue, setEditingReviewValue] = useState<string | null>(null);
  const [reviewReplacement, setReviewReplacement] = useState("");

  useEffect(() => {
    customerImportDrafts.set(draftKey, {
      step,
      file,
      headers,
      rows,
      mapping,
      selectedHeader,
      reviewField,
      reviewSort,
      rowOverrides,
      skippedFieldsByRow,
      skippedRowIndices,
      validation,
      preparedPayload,
      importSummary,
    });
  }, [
    draftKey,
    file,
    headers,
    importSummary,
    mapping,
    preparedPayload,
    reviewField,
    reviewSort,
    rowOverrides,
    rows,
    selectedHeader,
    skippedFieldsByRow,
    skippedRowIndices,
    step,
    validation,
  ]);

  const missingRequired = missingImportFields(mapping);
  const mappedFields = ALL_FIELDS.filter((field) => mapping[field.key]);
  const allPreparedRows = useMemo(
    () =>
      (applyMapping(rows, mapping) as PreparedRow[]).map((row) => ({
        ...row,
        ...rowOverrides[row.row_index],
      })),
    [mapping, rowOverrides, rows],
  );
  const preparedRows = useMemo(() => {
    const skipped = new Set(skippedRowIndices);
    return allPreparedRows.filter((row) => !skipped.has(row.row_index));
  }, [allPreparedRows, skippedRowIndices]);

  const validationByRow = useMemo(
    () => new Map(validation?.results.map((result) => [result.row_index, result]) ?? []),
    [validation],
  );

  const validRows = useMemo(() => {
    if (!validation) return [] as PreparedRow[];
    const validIndices = new Set(
      validation.results.filter((result) => result.ok).map((result) => result.row_index),
    );
    return preparedPayload.filter((row) => validIndices.has(row.row_index));
  }, [preparedPayload, validation]);
  // Keep large imports responsive by rendering a bounded preview. The counts
  // and commit still cover every validated row.
  const previewRows = useMemo(() => validRows.slice(0, 100), [validRows]);
  const previewUpdateCount = validation?.update_count ?? 0;
  // The ok_count fallback keeps rolling frontend/function deployments safe.
  const previewCreateCount = validation?.create_count ?? Math.max(
    0,
    (validation?.ok_count ?? 0) - previewUpdateCount,
  );
  const previewSkippedCount = Math.max(
    0,
    rows.length - previewCreateCount - previewUpdateCount,
  );

  const activeReviewField = mapping[reviewField]
    ? reviewField
    : mappedFields[0]?.key ?? "name";
  const activeReviewFieldIsRequired = REQUIRED_FIELDS.includes(activeReviewField);

  const reviewRows = useMemo(() => {
    const sourceHeader = mapping[activeReviewField];
    if (!sourceHeader) return [] as ReviewRow[];

    const skippedRows = new Set(skippedRowIndices);
    const nextRows = allPreparedRows.map((prepared) => {
      const sourceRow = rows[prepared.row_index - 2];
      const raw = String(sourceRow?.[sourceHeader] ?? "").trim();
      const mapped = String(prepared[activeReviewField] ?? "").trim();
      const result = validationByRow.get(prepared.row_index);
      const isRowSkipped = skippedRows.has(prepared.row_index);
      const isFieldSkipped =
        skippedFieldsByRow[prepared.row_index]?.includes(activeReviewField) ??
        false;
      const isSkipped =
        isFieldSkipped ||
        (!activeReviewFieldIsRequired &&
          !mapped);
      return {
        rowIndex: prepared.row_index,
        raw: raw || "Empty value",
        mapped: isSkipped ? "Skipped" : mapped || "Not set",
        hasError:
          !isRowSkipped &&
          (result?.errors.some((error) =>
            validationIssueMatchesField(error, activeReviewField),
          ) ??
            false),
        isSkipped,
      };
    });

    return nextRows.sort((a, b) => {
      if (reviewSort === "row") return a.rowIndex - b.rowIndex;
      if (reviewSort === "errors") {
        if (a.hasError !== b.hasError) return a.hasError ? -1 : 1;
        return a.rowIndex - b.rowIndex;
      }
      return a.raw.localeCompare(b.raw) || a.rowIndex - b.rowIndex;
    });
  }, [
    activeReviewField,
    activeReviewFieldIsRequired,
    allPreparedRows,
    mapping,
    reviewSort,
    rows,
    skippedFieldsByRow,
    skippedRowIndices,
    validationByRow,
  ]);

  const cancelImport = () => {
    customerImportDrafts.delete(draftKey);
    if (onCancel) {
      onCancel();
      return;
    }
    navigate("/?tab=customers");
  };

  const finishImport = () => {
    customerImportDrafts.delete(draftKey);
    if (onComplete) {
      onComplete();
      return;
    }
    navigate("/?tab=customers");
  };

  const clearFile = () => {
    setFile(null);
    setHeaders([]);
    setRows([]);
    setMapping(emptyMapping());
    setRowOverrides({});
    setSkippedFieldsByRow({});
    setSkippedRowIndices([]);
    setSelectedHeader(null);
    setParseError(null);
    setValidation(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleFile = async (nextFile: File) => {
    setParseError(null);
    setValidation(null);
    setImportSummary(null);

    try {
      const extension = nextFile.name.split(".").pop()?.toLowerCase() ?? "";
      let parsed: { headers: string[]; rows: ParsedRow[] };
      if (extension === "csv" || extension === "tsv" || extension === "txt") {
        parsed = await parseCsv(nextFile);
      } else if (extension === "xlsx" || extension === "xls") {
        parsed = await parseXlsx(nextFile);
      } else {
        throw new Error("Unsupported file type. Upload a CSV or Excel workbook.");
      }

      if (parsed.headers.length === 0) throw new Error("No column headers were found.");
      if (parsed.rows.length === 0) throw new Error(`The file has no ${customer.toLowerCase()} rows.`);
      if (parsed.rows.length > 5000) {
        throw new Error("Imports support up to 5,000 rows. Split this file and try again.");
      }

      const suggestedMapping = autoMapHeaders(parsed.headers);
      setFile(nextFile);
      setHeaders(parsed.headers);
      setRows(parsed.rows);
      setMapping(suggestedMapping);
      setRowOverrides({});
      setSkippedFieldsByRow({});
      setSkippedRowIndices([]);
      setSelectedHeader(parsed.headers[0] ?? null);
      setReviewField(
        ALL_FIELDS.find((field) => suggestedMapping[field.key])?.key ?? "name",
      );
    } catch (error) {
      clearFile();
      setParseError(error instanceof Error ? error.message : String(error));
    }
  };

  const setHeaderMapping = (header: string, fieldValue: string) => {
    setMapping((current) => {
      const next = { ...current };

      for (const field of ALL_FIELDS) {
        if (next[field.key] === header) next[field.key] = null;
      }

      if (fieldValue !== UNMAPPED) {
        next[fieldValue as ContactField] = header;
      }
      return next;
    });
    setRowOverrides({});
    setSkippedFieldsByRow({});
    setSkippedRowIndices([]);
    setPreparedPayload([]);
    setValidation(null);
  };

  const mappedFieldForHeader = (header: string) =>
    ALL_FIELDS.find((field) => mapping[field.key] === header)?.key ?? UNMAPPED;

  const requestValidation = async (payload: PreparedRow[]) => {
    if (!businessId) {
      throw new Error("Your business is still loading. Try again in a moment.");
    }

    if (payload.length === 0) {
      return {
        total: 0,
        ok_count: 0,
        error_count: 0,
        create_count: 0,
        update_count: 0,
        skip_count: 0,
        results: [],
      } satisfies ValidationResult;
    }

    const { data, error } = await supabase.functions.invoke("contacts-import", {
      body: {
        mode: "validate",
        business_id: businessId,
        rows: payload,
      },
    });
    if (error) throw new Error(await readEdgeError(error, "The import service could not validate this file."));
    if (data?.error) throw new Error(data.error);
    return data as ValidationResult;
  };

  const runValidation = async () => {
    setValidating(true);
    setValidation(null);
    try {
      const payload = preparedRows;
      const result = await requestValidation(payload);
      setPreparedPayload(payload);
      setValidation(result);
      setReviewField(mappedFields[0]?.key ?? "name");
      setStep(3);
    } catch (error) {
      toast.error(`Validation failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setValidating(false);
    }
  };

  const revalidateReview = async (payload: PreparedRow[]) => {
    setValidating(true);
    try {
      const result = await requestValidation(payload);
      setPreparedPayload(payload);
      setValidation(result);
    } catch (error) {
      toast.error(
        `Validation failed: ${error instanceof Error ? error.message : String(error)}`,
      );
    } finally {
      setValidating(false);
    }
  };

  const skipReviewValue = (rowIndex: number) => {
    if (activeReviewFieldIsRequired) {
      const nextOverrides = {
        ...rowOverrides,
        [rowIndex]: {
          ...rowOverrides[rowIndex],
          [activeReviewField]: null,
        },
      };
      const nextSkippedFields = { ...skippedFieldsByRow };
      nextSkippedFields[rowIndex] = Array.from(
        new Set([...(nextSkippedFields[rowIndex] ?? []), activeReviewField]),
      );

      setRowOverrides(nextOverrides);
      setSkippedFieldsByRow(nextSkippedFields);
      setSkippedRowIndices((current) =>
        current.includes(rowIndex) ? current : [...current, rowIndex],
      );
      setEditingReviewValue(null);
      setReviewReplacement("");
      void revalidateReview(
        preparedRows.filter((row) => row.row_index !== rowIndex),
      );
      return;
    }

    const nextOverrides = {
      ...rowOverrides,
      [rowIndex]: {
        ...rowOverrides[rowIndex],
        [activeReviewField]: null,
      },
    };
    const nextSkippedFields = { ...skippedFieldsByRow };
    nextSkippedFields[rowIndex] = Array.from(
      new Set([...(nextSkippedFields[rowIndex] ?? []), activeReviewField]),
    );
    const nextPayload = preparedRows.map((row) =>
      row.row_index === rowIndex
        ? { ...row, [activeReviewField]: null }
        : row,
    );

    setRowOverrides(nextOverrides);
    setSkippedFieldsByRow(nextSkippedFields);
    setEditingReviewValue(null);
    setReviewReplacement("");
    void revalidateReview(nextPayload);
  };

  const replaceReviewValue = (rowIndices: number[], value: string) => {
    const replacement = value.trim();
    if (!replacement) return;

    const affectedRows = new Set(rowIndices);
    const nextOverrides = { ...rowOverrides };
    for (const rowIndex of rowIndices) {
      nextOverrides[rowIndex] = {
        ...nextOverrides[rowIndex],
        [activeReviewField]: replacement,
      };
    }
    const nextSkippedFields = { ...skippedFieldsByRow };
    for (const rowIndex of rowIndices) {
      nextSkippedFields[rowIndex] =
        nextSkippedFields[rowIndex]?.filter((field) => field !== activeReviewField) ?? [];
    }
    const nextSkippedRows = activeReviewFieldIsRequired
      ? skippedRowIndices.filter((rowIndex) => {
          if (!affectedRows.has(rowIndex)) return true;
          return (nextSkippedFields[rowIndex] ?? []).some((field) =>
            REQUIRED_FIELDS.includes(field),
          );
        })
      : skippedRowIndices;
    const skippedRows = new Set(nextSkippedRows);
    const nextPayload = allPreparedRows
      .filter((row) => !skippedRows.has(row.row_index))
      .map((row) =>
        affectedRows.has(row.row_index)
          ? { ...row, [activeReviewField]: replacement }
          : row,
      );

    setRowOverrides(nextOverrides);
    setSkippedFieldsByRow(nextSkippedFields);
    setSkippedRowIndices(nextSkippedRows);
    setEditingReviewValue(null);
    setReviewReplacement("");
    void revalidateReview(nextPayload);
  };

  const finishReviewValueEdit = (rowIndex: number) => {
    const replacement = reviewReplacement.trim();
    if (replacement) {
      replaceReviewValue([rowIndex], replacement);
      return;
    }
    setEditingReviewValue(null);
    setReviewReplacement("");
  };

  const runCommit = async () => {
    if (!businessId || validRows.length === 0) return;
    setCommitting(true);

    try {
      const { data, error } = await supabase.functions.invoke("contacts-import", {
        body: {
          mode: "commit",
          business_id: businessId,
          rows: preparedPayload,
        },
      });
      if (error) throw new Error(await readEdgeError(error, `The import service could not import these ${customers.toLowerCase()}.`));
      if (data?.error) throw new Error(data.error);

      const inserted = data.inserted ?? 0;
      const updated = data.updated ?? 0;
      const writeErrorCount = data.error_count ?? 0;
      const explicitlySkipped = Math.max(0, rows.length - preparedPayload.length);
      const serverSkipped = data.skipped_count ?? Math.max(
        0,
        preparedPayload.length - inserted - updated - writeErrorCount,
      );
      const summary: ImportSummary = {
        inserted,
        updated,
        error_count: writeErrorCount,
        skipped_count: explicitlySkipped + serverSkipped,
        profile_error_count: data.profile_error_count ?? 0,
        insert_errors: data.insert_errors ?? [],
      };
      setImportSummary(summary);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["customers"] }),
        queryClient.invalidateQueries({ queryKey: ["customer-sample-state", businessId] }),
      ]);

      const importedCount = summary.inserted + summary.updated;
      if (importedCount > 0) {
        toast.success(
          `Imported ${importedCount} ${importedCount === 1 ? customer.toLowerCase() : customers.toLowerCase()} (${summary.inserted} created, ${summary.updated} updated)`,
        );
      } else {
        const firstError = summary.insert_errors?.[0]?.message;
        toast.error(firstError ? `Import failed: ${firstError}` : `No ${customers.toLowerCase()} were imported.`);
      }
    } catch (error) {
      toast.error(`Import failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setCommitting(false);
    }
  };

  const goBack = () => {
    if (step === 2) setStep(1);
    if (step === 3) setStep(2);
    if (step === 4) setStep(3);
  };

  return (
    <div
      className={cn(
        "flex min-h-0 flex-col bg-card",
        embedded
          ? "h-full"
          : "-mx-4 -mt-4 h-[calc(100%+2rem)] md:-mx-6 md:h-[calc(100%+2.5rem)]",
      )}
    >
      <div className="flex h-12 shrink-0 items-center gap-4 border-b border-border bg-card px-4 md:px-6">
        <nav className="flex min-w-0 flex-1 items-center overflow-x-auto" aria-label="Import progress">
          {STEPS.map((item, index) => {
            const isActive = item.value === step;
            const isComplete = item.value < step;
            const canVisit = item.value < step && !importSummary;
            return (
              <div key={item.value} className="flex shrink-0 items-center">
                <button
                  type="button"
                  className={cn(
                    "flex h-12 items-center gap-2 text-sm font-normal transition-colors",
                    isActive
                      ? "font-medium text-foreground"
                      : isComplete
                        ? "text-foreground"
                        : "text-muted-foreground",
                    canVisit && "hover:text-foreground",
                  )}
                  disabled={!canVisit}
                  onClick={() => canVisit && setStep(item.value)}
                >
                  <span
                    className={cn(
                      "flex h-5 w-5 items-center justify-center rounded-full border text-[11px] font-semibold",
                      isActive && "border-primary bg-primary/10 text-primary",
                      isComplete && "border-primary bg-primary text-primary-foreground",
                      !isActive && !isComplete && "border-border bg-muted text-muted-foreground",
                    )}
                  >
                    {isComplete ? <Check className="h-3 w-3" /> : item.value}
                  </span>
                  {item.label}
                </button>
                {index < STEPS.length - 1 && (
                  <ArrowRight className="mx-4 h-3.5 w-3.5 text-muted-foreground/60" />
                )}
              </div>
            );
          })}
        </nav>
        <button
          type="button"
          className="shrink-0 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none"
          aria-label="Cancel import"
          title="Cancel import"
          onClick={cancelImport}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <main className="min-h-0 flex-1 overflow-hidden bg-card">
        {step === 1 && (
          <UploadStage
            file={file}
            headers={headers}
            rows={rows}
            parseError={parseError}
            dragActive={dragActive}
            inputRef={inputRef}
            onDragActiveChange={setDragActive}
            onFile={handleFile}
            onClear={clearFile}
          />
        )}

        {step === 2 && (
          <div className="grid h-full min-h-0 overflow-y-auto lg:grid-cols-[minmax(520px,1fr)_minmax(320px,0.65fr)] lg:overflow-hidden">
            <section className="min-h-0 overflow-visible border-r border-border lg:overflow-y-auto">
              <div className="grid grid-cols-[minmax(100px,0.75fr)_minmax(150px,1fr)] border-b border-border bg-muted/20 px-4 py-2 text-xs font-medium text-muted-foreground sm:grid-cols-[minmax(0,1fr)_minmax(230px,0.8fr)] md:px-6">
                <span className="relative left-px">File column</span>
                <span>Dibs {customer.toLowerCase()} field</span>
              </div>
              {headers.map((header) => {
                const field = mappedFieldForHeader(header);
                return (
                  <button
                    type="button"
                    key={header}
                    className={cn(
                      "grid w-full grid-cols-[minmax(100px,0.75fr)_minmax(150px,1fr)] items-center gap-2 border-b border-border px-4 py-2.5 text-left transition-colors sm:grid-cols-[minmax(0,1fr)_minmax(230px,0.8fr)] sm:gap-4 md:px-6",
                      selectedHeader === header ? "bg-primary/[0.035]" : "hover:bg-muted/30",
                    )}
                    onClick={() => setSelectedHeader(header)}
                  >
                    <div className="relative left-px flex min-w-0 items-center gap-3">
                      <span className="truncate text-sm font-medium">{header}</span>
                      <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
                    </div>
                    <Select
                      value={field}
                      onValueChange={(value) => setHeaderMapping(header, value)}
                    >
                      <SelectTrigger
                        className="h-9 bg-card"
                        onClick={(event) => event.stopPropagation()}
                        aria-label={`Map ${header}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={UNMAPPED}>Do not import</SelectItem>
                        {ALL_FIELDS.map((option) => (
                          <SelectItem key={option.key} value={option.key}>
                            {option.label}{option.required ? " *" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </button>
                );
              })}
              <div className="space-y-3 px-4 py-4 md:px-6">
                <p className="flex items-start gap-2 text-sm text-muted-foreground">
                  <RefreshCcw className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                  Existing {customers.toLowerCase()} with the same normalized email or phone are updated automatically. Rows whose email and phone point to different people are held for review.
                </p>
                {missingRequired.length > 0 && (
                  <p className="flex items-center gap-2 text-sm text-foreground">
                    <AlertCircle className="h-4 w-4 shrink-0 text-amber-600" />
                    <span>
                      Map these fields to continue:{" "}
                      <span className="font-medium">
                        {missingRequired.join(", ")}
                      </span>
                    </span>
                  </p>
                )}
              </div>
            </section>

            <aside className="min-h-0 overflow-visible bg-card p-3 sm:p-5 md:px-6 lg:overflow-y-auto">
              <div className="overflow-hidden rounded-xl border border-border bg-card">
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{selectedHeader ?? "Data preview"}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">First 15 values from this column</p>
                  </div>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Eye className="h-3.5 w-3.5" />
                    Data preview
                  </span>
                </div>
                {selectedHeader ? (
                  rows.slice(0, 15).map((row, index) => (
                    <div
                      key={index}
                      className="min-h-11 border-b border-border px-4 py-2.5 text-sm last:border-b-0"
                    >
                      {String(row[selectedHeader] ?? "") || (
                        <span className="text-muted-foreground">Empty value</span>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="px-4 py-10 text-center text-sm text-muted-foreground">
                    Select a file column to preview its values.
                  </p>
                )}
              </div>
              <p className="mt-3 text-center text-xs text-muted-foreground">
                This preview shows only a portion of the column values.
              </p>
            </aside>
          </div>
        )}

        {step === 3 && validation && (
          <div className="block h-full min-h-0 overflow-y-auto md:grid md:grid-cols-[300px_minmax(0,1fr)] md:overflow-hidden">
            <aside className="min-h-0 border-r border-border p-4 md:overflow-y-auto md:pl-6">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold">Columns</h2>
                <span className="text-xs text-muted-foreground">{mappedFields.length} mapped</span>
              </div>
              <div className="space-y-2">
                {mappedFields.map((field) => {
                  const Icon = FIELD_ICONS[field.key];
                  const selected = activeReviewField === field.key;
                  return (
                    <button
                      type="button"
                      key={field.key}
                      className={cn(
                        "w-full rounded-lg border p-3 text-left transition-colors",
                        selected
                          ? "border-primary bg-primary/[0.035] ring-1 ring-primary/20"
                          : "border-border bg-card hover:bg-muted/30",
                      )}
                      onClick={() => {
                        setReviewField(field.key);
                        setEditingReviewValue(null);
                        setReviewReplacement("");
                      }}
                    >
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                        <span className="truncate">{mapping[field.key]}</span>
                      </div>
                      <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                        <Icon className="h-4 w-4" />
                        <span className="truncate">{field.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </aside>

            <section className="min-h-0 md:overflow-y-auto">
              <div className="flex items-center border-b border-border px-4 py-3 md:px-6">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="admin-toolbar-button relative -left-px"
                    >
                      <ArrowUpDown className="h-3.5 w-3.5" />
                      Sort by{" "}
                      {reviewSort === "raw"
                        ? "Raw value"
                        : reviewSort === "row"
                          ? "File row"
                          : "Errors first"}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={() => setReviewSort("raw")}>
                      Raw value
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setReviewSort("row")}>
                      File row
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setReviewSort("errors")}>
                      Errors first
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="grid grid-cols-[minmax(0,1fr)_80px_minmax(0,1fr)] border-b border-border bg-muted/20 px-4 py-2 text-xs font-medium text-muted-foreground md:px-6">
                <span>Raw value</span>
                <span />
                <span>Mapped value</span>
              </div>
              {reviewRows.length > 0 ? (
                reviewRows.map((reviewRow) => {
                  const editKey = `${activeReviewField}:${reviewRow.rowIndex}`;
                  const isEditing = editingReviewValue === editKey;
                  const beginEditingReviewValue = () => {
                    setEditingReviewValue(editKey);
                    setReviewReplacement(
                      reviewRow.mapped === "Not set" ||
                        reviewRow.mapped === "Skipped"
                        ? ""
                        : reviewRow.mapped,
                    );
                  };
                  return (
                    <div
                      key={editKey}
                      className="grid min-h-14 grid-cols-[minmax(0,1fr)_80px_minmax(0,1fr)] items-center border-b border-border px-4 py-2 md:px-6"
                    >
                      <div className="min-w-0">
                        <div className="flex min-w-0 items-baseline gap-2">
                          <p className="truncate text-sm font-medium">{reviewRow.raw}</p>
                          <span className="shrink-0 text-xs text-muted-foreground">
                            Row {reviewRow.rowIndex}
                          </span>
                          {reviewRow.hasError && (
                            <span className="shrink-0 text-xs text-destructive">Invalid</span>
                          )}
                        </div>
                      </div>
                      <ArrowRight className="mx-auto h-4 w-4 text-muted-foreground" />
                      <div className="min-w-0">
                        {isEditing ? (
                          <div
                            className="flex h-9 min-w-0 overflow-hidden rounded-md border border-input bg-card"
                          >
                            <Input
                              autoFocus
                              value={reviewReplacement}
                              className="h-full min-w-0 rounded-none border-0 bg-transparent px-3 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                              placeholder={`Enter ${ALL_FIELDS.find((field) => field.key === activeReviewField)?.label ?? "value"}`}
                              onChange={(event) => setReviewReplacement(event.target.value)}
                              onBlur={() => finishReviewValueEdit(reviewRow.rowIndex)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                  event.preventDefault();
                                  finishReviewValueEdit(reviewRow.rowIndex);
                                }
                                if (event.key === "Escape") {
                                  setEditingReviewValue(null);
                                  setReviewReplacement("");
                                }
                              }}
                            />
                            <span
                              className="flex h-full w-9 shrink-0 items-center justify-center border-l border-border text-muted-foreground"
                              aria-hidden="true"
                            >
                              {reviewRow.hasError ? (
                                <CircleSlash className="h-3.5 w-3.5" />
                              ) : (
                                <Plus className="h-3.5 w-3.5" />
                              )}
                            </span>
                          </div>
                        ) : (
                          <div
                            className={cn(
                              "flex h-9 min-w-0 overflow-hidden rounded-md border bg-card text-sm",
                              reviewRow.hasError
                                ? "border-destructive/40"
                                : "border-border",
                            )}
                          >
                            <button
                              type="button"
                              className="flex min-w-0 flex-1 items-center gap-2 px-3 text-left disabled:cursor-default"
                              disabled={validating}
                              title="Edit value"
                              onClick={beginEditingReviewValue}
                            >
                              {reviewRow.isSkipped ? (
                                <CircleSlash className="h-4 w-4 shrink-0 text-muted-foreground" />
                              ) : reviewRow.hasError ? (
                                <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
                              ) : (
                                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                              )}
                              <span className="truncate">{reviewRow.mapped}</span>
                            </button>
                            <button
                              type="button"
                              className="flex h-full w-9 shrink-0 items-center justify-center border-l border-border text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                              disabled={validating}
                              title={
                                reviewRow.isSkipped ? "Add value" : "Skip value"
                              }
                              aria-label={`${reviewRow.isSkipped ? "Add value to" : "Skip value in"} row ${reviewRow.rowIndex}`}
                              onClick={() => {
                                if (reviewRow.isSkipped) {
                                  beginEditingReviewValue();
                                } else {
                                  skipReviewValue(reviewRow.rowIndex);
                                }
                              }}
                            >
                              {reviewRow.isSkipped ? (
                                <Plus className="h-3.5 w-3.5" />
                              ) : (
                                <CircleSlash className="h-3.5 w-3.5" />
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="px-6 py-14 text-center">
                  <p className="text-sm font-medium">No rows left to review</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Go back to include or edit {customer.toLowerCase()} rows.
                  </p>
                </div>
              )}
            </section>
          </div>
        )}

        {step === 4 && validation && (
          importSummary ? (
            <ImportComplete
              summary={importSummary}
              onDone={finishImport}
              buttonLabel={embedded ? "Return to onboarding" : `Back to ${customers.toLowerCase()}`}
              customer={customer}
              customers={customers}
            />
          ) : (
            <div className="h-full overflow-y-auto px-4 pb-4 pt-2 md:px-6 md:pb-6">
              <div className="w-full">
                <div className="mb-2 flex min-h-7 flex-wrap items-center gap-x-6 gap-y-1">
                  <div className="inline-flex items-center gap-1.5 text-sm">
                    <Plus className="h-3.5 w-3.5 text-emerald-600" />
                    <span className="font-semibold text-foreground">{previewCreateCount}</span>
                    <span className="text-muted-foreground">will be created</span>
                  </div>
                  <div className="inline-flex items-center gap-1.5 text-sm">
                    <RefreshCcw className="h-3.5 w-3.5 text-blue-600" />
                    <span className="font-semibold text-foreground">{previewUpdateCount}</span>
                    <span className="text-muted-foreground">will be updated</span>
                  </div>
                  <div className="inline-flex items-center gap-1.5 text-sm">
                    <CircleSlash className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="font-semibold text-foreground">{previewSkippedCount}</span>
                    <span className="text-muted-foreground">will be skipped</span>
                  </div>
                </div>

                {previewRows.length > 0 ? (
                  <>
                    <div className="admin-data-table-shell">
                      <div className="admin-data-table-scroll w-full overflow-hidden bg-card">
                        <Table className="admin-data-table min-w-[900px]">
                          <TableHeader className="admin-data-table-header">
                            <TableRow className="admin-data-table-header-row">
                              <TableHead className="w-20 px-3">Row</TableHead>
                              <TableHead className="w-36 px-3">Action</TableHead>
                              {mappedFields.map((field) => (
                                <TableHead key={field.key} className="whitespace-nowrap px-3">
                                  {field.label}
                                </TableHead>
                              ))}
                            </TableRow>
                          </TableHeader>
                          <TableBody className="admin-data-table-body">
                            {previewRows.map((row) => {
                              const decision = validationByRow.get(row.row_index);
                              return (
                                <TableRow key={row.row_index} className="admin-data-table-row">
                                  <TableCell className="admin-data-table-cell text-xs text-muted-foreground">
                                    {row.row_index}
                                  </TableCell>
                                  <TableCell className="admin-data-table-cell">
                                    <Badge variant={decision?.action === "update" ? "secondary" : "outline"}>
                                      {decision?.action === "update"
                                        ? `Update${decision.match_reason ? ` · ${decision.match_reason === "email_and_phone" ? "email + phone" : decision.match_reason}` : ""}`
                                        : "Create"}
                                    </Badge>
                                    {decision?.action === "update" && (
                                      <p className="mt-1 max-w-44 truncate text-[11px] text-muted-foreground" title={decision.changed_fields.join(", ")}>
                                        {decision.changed_fields.length > 0
                                          ? `Changes: ${decision.changed_fields.join(", ")}`
                                          : "No detail changes"}
                                      </p>
                                    )}
                                  </TableCell>
                                  {mappedFields.map((field) => (
                                    <TableCell
                                      key={field.key}
                                      className="admin-data-table-cell max-w-[280px] truncate text-sm"
                                    >
                                      {String(row[field.key] ?? "") || (
                                        <span className="text-muted-foreground">—</span>
                                      )}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <p className="text-xs font-medium tracking-wide text-muted-foreground">
                        Showing {previewRows.length} of {validRows.length} {customers.toLowerCase()}
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="rounded-xl border border-dashed border-border px-6 py-16 text-center">
                    <AlertCircle className="mx-auto h-8 w-8 text-destructive" />
                    <h3 className="mt-3 text-sm font-semibold">No {customers.toLowerCase()} are ready to import</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Go back to review the validation errors.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )
        )}
      </main>

      {!importSummary && (
        <footer className="flex h-[63px] shrink-0 items-center justify-end border-t border-border bg-card px-4 md:px-6">
          <div className="flex items-center gap-4">
            {step > 1 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={goBack}
                disabled={validating || committing}
              >
                <ArrowLeft className="mr-1.5 h-4 w-4" />
                Back
              </Button>
            )}
            {step === 1 && (
              <Button type="button" size="sm" disabled={!file} onClick={() => setStep(2)}>
                Next
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            )}
            {step === 2 && (
              <Button
                type="button"
                size="sm"
                disabled={missingRequired.length > 0 || validating}
                onClick={runValidation}
              >
                {validating && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                Next
                {!validating && <ArrowRight className="ml-1.5 h-4 w-4" />}
              </Button>
            )}
            {step === 3 && (
              <Button
                type="button"
                size="sm"
                disabled={validating}
                onClick={() => setStep(4)}
              >
                Next
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            )}
            {step === 4 && (
              <Button
                type="button"
                size="sm"
                disabled={validRows.length === 0 || committing}
                onClick={runCommit}
              >
                {committing && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                Import {validRows.length} {validRows.length === 1 ? customer.toLowerCase() : customers.toLowerCase()}
              </Button>
            )}
          </div>
        </footer>
      )}
    </div>
  );
}

function validationIssueMatchesField(issue: string, field: ContactField) {
  const normalized = issue.toLowerCase();
  if (field === "name") return normalized.startsWith("name ");
  if (field === "email") {
    return (
      normalized.startsWith("email ") ||
      normalized.includes("valid email") ||
      normalized.includes("add an email") ||
      normalized.includes("duplicate")
    );
  }
  if (field === "phone") {
    return normalized.startsWith("phone ") || normalized.includes("add an email or a phone");
  }
  if (field === "notes") return normalized.startsWith("notes ");
  return normalized.startsWith("last_booking_date ");
}

function UploadStage({
  file,
  headers,
  rows,
  parseError,
  dragActive,
  inputRef,
  onDragActiveChange,
  onFile,
  onClear,
}: {
  file: File | null;
  headers: string[];
  rows: ParsedRow[];
  parseError: string | null;
  dragActive: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  onDragActiveChange: (active: boolean) => void;
  onFile: (file: File) => void;
  onClear: () => void;
}) {
  return (
    <div className="h-full overflow-y-auto p-5 md:p-7">
      <div className="mx-auto flex min-h-full max-w-5xl flex-col justify-center">
        <div
          className={cn(
            "flex min-h-[360px] flex-col items-center justify-center rounded-2xl border border-dashed p-8 text-center transition-colors",
            dragActive ? "border-primary bg-primary/[0.035]" : "border-border bg-card",
          )}
          onDragEnter={(event) => {
            event.preventDefault();
            onDragActiveChange(true);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            onDragActiveChange(true);
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            if (event.currentTarget === event.target) onDragActiveChange(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            onDragActiveChange(false);
            const nextFile = event.dataTransfer.files?.[0];
            if (nextFile) void onFile(nextFile);
          }}
        >
          {!file ? (
            <>
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <UploadCloud className="h-6 w-6" />
              </span>
              <p className="mt-4 text-sm font-medium text-foreground">
                 Drop a file here to upload
              </p>
              <div className="my-4 flex w-full max-w-xs items-center gap-3 text-xs text-muted-foreground">
                <span className="h-px flex-1 bg-border" />
                <span>or</span>
                <span className="h-px flex-1 bg-border" />
              </div>
              <Button
                type="button"
                variant="outline"
                className="neutral-action-button h-7 w-full max-w-[240px] rounded-md bg-card text-sm shadow-none"
                onClick={() => inputRef.current?.click()}
              >
                Choose a file
              </Button>
              <p className="mt-3 text-xs text-muted-foreground">.CSV or .XLSX file types · Up to 5,000 rows</p>
            </>
          ) : (
            <div className="w-full max-w-md rounded-xl border border-border bg-background p-4 text-left shadow-sm">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <FileSpreadsheet className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{file.name}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {formatFileSize(file.size)}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground"
                  aria-label="Remove file"
                  onClick={onClear}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 border-t border-border pt-4">
                <div className="rounded-lg border border-border bg-card p-3">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Columns3 className="h-3.5 w-3.5" />
                    Columns found
                  </div>
                  <p className="mt-1 text-2xl font-semibold">{headers.length}</p>
                </div>
                <div className="rounded-lg border border-border bg-card p-3">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Rows3 className="h-3.5 w-3.5" />
                    Rows found
                  </div>
                  <p className="mt-1 text-2xl font-semibold">{rows.length}</p>
                </div>
              </div>
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".csv,.tsv,.txt,.xlsx,.xls"
            className="sr-only"
            onChange={(event) => {
              const nextFile = event.target.files?.[0];
              if (nextFile) void onFile(nextFile);
            }}
          />
        </div>
        {parseError && (
          <p className="mt-3 flex items-center justify-center gap-1.5 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            {parseError}
          </p>
        )}
      </div>
    </div>
  );
}

function ImportComplete({
  summary,
  onDone,
  buttonLabel,
  customer,
  customers,
}: {
  summary: ImportSummary;
  onDone: () => void;
  buttonLabel: string;
  customer: string;
  customers: string;
}) {
  const importedCount = summary.inserted + summary.updated;
  const succeeded = importedCount > 0;
  return (
    <div className="flex h-full items-center justify-center overflow-y-auto p-6">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
        <span
          className={cn(
            "mx-auto flex h-12 w-12 items-center justify-center rounded-full",
            succeeded ? "bg-emerald-50 text-emerald-600" : "bg-destructive/10 text-destructive",
          )}
        >
          {succeeded ? <CheckCircle2 className="h-6 w-6" /> : <AlertCircle className="h-6 w-6" />}
        </span>
        <h2 className="mt-4 text-lg font-semibold">
          {succeeded
            ? `Imported ${importedCount} ${importedCount === 1 ? customer.toLowerCase() : customers.toLowerCase()}`
            : `No ${customers.toLowerCase()} were imported`}
        </h2>
        {succeeded && (
          <p className="mt-2 text-sm text-muted-foreground">
            {summary.inserted} created · {summary.updated} updated
          </p>
        )}
        {summary.skipped_count > 0 && (
          <p className="mt-2 text-sm text-muted-foreground">
            {summary.skipped_count} row{summary.skipped_count === 1 ? " was" : "s were"} skipped.
          </p>
        )}
        {summary.error_count > 0 && (
          <div className="mt-2 text-sm text-destructive">
            <p>
              {summary.error_count} row{summary.error_count === 1 ? "" : "s"} failed to import.
            </p>
            {summary.insert_errors?.[0]?.message && (
              <p className="mt-1 text-xs">{summary.insert_errors[0].message}</p>
            )}
          </div>
        )}
        {summary.profile_error_count > 0 && (
          <p className="mt-2 text-sm text-amber-700">
            {summary.profile_error_count} AI profile
            {summary.profile_error_count === 1 ? "" : "s"} will need to be retried.
          </p>
        )}
        <Button type="button" className="mt-6" onClick={onDone}>
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
}

async function parseCsv(file: File): Promise<{ headers: string[]; rows: ParsedRow[] }> {
  return new Promise((resolve, reject) => {
    Papa.parse<ParsedRow>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        if (result.errors.length > 0 && result.data.length === 0) {
          reject(new Error(result.errors[0].message));
          return;
        }
        resolve({
          headers: (result.meta.fields ?? []).filter((header) => header && header.trim()),
          rows: result.data,
        });
      },
      error: (error) => reject(error),
    });
  });
}

async function parseXlsx(file: File): Promise<{ headers: string[]; rows: ParsedRow[] }> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const worksheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!worksheet) throw new Error("No worksheet was found in this workbook.");
  const rows = XLSX.utils.sheet_to_json<ParsedRow>(worksheet, { defval: "" });
  return { headers: rows[0] ? Object.keys(rows[0]) : [], rows };
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
